import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { QrCode, WifiOff, RefreshCw, CheckCircle2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { queueCheckin, pendingCount, syncQueue } from "../lib/offlineQueue";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function CheckIn() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(0);
  const [result, setResult] = useState(null);

  const refreshPending = async () => setPending(await pendingCount());

  useEffect(() => {
    refreshPending();
    let alive = true;
    const onOnline = async () => {
      if (!alive) return;
      const n = await syncQueue();
      if (n > 0) toast.success(`${n} × ${t("checkin.title")}`);
      refreshPending();
    };
    window.addEventListener("online", onOnline);
    if (navigator.onLine) onOnline();
    return () => { alive = false; window.removeEventListener("online", onOnline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const value = code.trim().toUpperCase();
    if (!value) return;
    setBusy(true);
    if (!navigator.onLine) {
      await queueCheckin({ code: value, client_timestamp: new Date().toISOString() });      await refreshPending();
      setBusy(false);
      toast.info(t("checkin.offlineQueued"));
      return;
    }
    try {
      const { data } = await api.post("/checkin", { code: value, client_timestamp: new Date().toISOString() });
      setResult(data);
      toast.success(data.already ? t("checkin.already") : t("checkin.success"));
      setCode("");
    } catch (err) {
      if (!err.response) {
        await queueCheckin({ code: value, client_timestamp: new Date().toISOString() });
        await refreshPending();
        toast.info(t("checkin.offlineQueued"));
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="checkin-page">
      <h1 className="font-display text-2xl font-extrabold">{t("checkin.title")}</h1>

      <div className="vy-card p-6 text-center">
        <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-2xl bg-muted">
          <QrCode className="h-14 w-14 text-primary" />
        </div>
        <p className="mt-4 font-display text-lg font-bold">{t("checkin.scan")}</p>
        <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          data-testid="scanner-mock-note">
          MOCKED — {t("checkin.scannerMock")}
        </p>
      </div>

      <form onSubmit={submit} className="vy-card space-y-3 p-5" data-testid="checkin-form">
        <label className="text-sm font-semibold">{t("checkin.code")}</label>
        <Input data-testid="checkin-code-input" value={code} placeholder="VYTA26"
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className="h-14 text-center font-display text-2xl font-bold tracking-[0.3em]" />
        <Button data-testid="checkin-submit-btn" type="submit" disabled={busy}
          className="h-14 w-full rounded-full bg-accent text-base font-bold text-accent-foreground hover:bg-accent/90 active:scale-95">
          {busy ? t("common.loading") : t("checkin.validate")}
        </Button>
      </form>

      {result && (
        <div className="vy-card flex items-center gap-3 p-5" data-testid="checkin-result">
          <CheckCircle2 className="h-6 w-6 text-[hsl(var(--success))]" />
          <div>
            <p className="font-display font-bold">{result.event?.title}</p>
            <p className="text-xs text-muted-foreground">
              {result.already ? t("checkin.already") : t("checkin.success")}
            </p>
          </div>
        </div>
      )}

      {pending > 0 && (
        <div className="vy-card flex items-center gap-3 p-4" data-testid="offline-queue-banner">
          <WifiOff className="h-5 w-5 text-accent" />
          <p className="flex-1 text-sm font-semibold">{pending} {t("checkin.pendingSync")}</p>
          <Button data-testid="offline-sync-btn" variant="outline" className="h-10 rounded-full"
            onClick={async () => { const n = await syncQueue(); await refreshPending(); if (n) toast.success(t("checkin.success")); }}>
            <RefreshCw className="mr-1 h-4 w-4" />{t("checkin.syncNow")}
          </Button>
        </div>
      )}
    </div>
  );
}
